import {BARE_URL,splitMarkdownBareUrl} from '../shared/markdown-urls.js';
import {formattingShortcut} from './composer-formatting';

type Mark='bold'|'italic'|'underline';
const order:Mark[]=['bold','italic','underline'];
const marker:Record<Mark,string>={bold:'**',italic:'*',underline:'__'};
type Point={node:Node;offset:number;source:number;plain:number};
export type MessageInput=HTMLDivElement&{
 value:string;selectionStart:number;selectionEnd:number;readOnly:boolean;disabled:boolean;
 setSelectionRange(start:number,end:number):void;
 setRangeText(value:string,start:number,end:number,mode?:'end'):void;
};
/** Only B/U/I become DOM elements. Everything else, including HTML, URLs and
 * unsupported Markdown, remains text. No untrusted innerHTML or pasted markup. */
function paint(host:HTMLElement,source:string):void {
 host.replaceChildren();
 let prefix='\uE000';while(source.includes(prefix))prefix+='\uE000';
 const atoms:string[]=[];
 const atom=(raw:string)=>{const id=atoms.push(raw)-1;return `${prefix}${id}\uE001`;};
 let safe=source.replace(/```[\s\S]*?```|`[^`\n]+`|\[[^\]\n]+\]\([^\s)]+\)/g,atom);
 safe=safe.replace(BARE_URL,(match,_scheme,offset)=>{const parts=splitMarkdownBareUrl(safe,match,offset);return parts?atom(parts.link)+parts.tail:match;});
 const plain=(parent:Node,text:string)=>{
  const parts=text.split(new RegExp(`${prefix}(\\d+)\uE001`,'g'));
  parts.forEach((part,index)=>parent.appendChild(document.createTextNode(index%2===1?atoms[Number(part)]??'':part)));
 };
 const inline=(parent:Node,text:string,depth=0):void=>{
  if(depth>16){plain(parent,text);return;}
  const pattern=/(\*\*\*|\*\*|__|\*)([^\n]+?)\1/g;let from=0;
  for(const match of text.matchAll(pattern)) {
   const at=match.index!;plain(parent,text.slice(from,at));
   const tag=match[1]==='__'?'u':match[1]==='*'?'em':'strong';const child=document.createElement(tag);parent.appendChild(child);
   if(match[1]==='***'){const em=document.createElement('em');child.append(em);inline(em,match[2]!,depth+1);}else inline(child,match[2]!,depth+1);
   from=at+match[0].length;
  }
  plain(parent,text.slice(from));
 };
 inline(host,safe);
}
function snapshot(host:HTMLElement):{value:string;points:Point[]} {
 type Unit={text:string;marks:Mark[];node:Node;offset:number};const units:Unit[]=[];
 const collect=(node:Node,inherited:Mark[]):void=>{
  if(node.nodeType===Node.TEXT_NODE){const text=node.textContent??'';for(let i=0;i<text.length;i++)units.push({text:text[i]!,marks:inherited,node,offset:i});return;}
  if(!(node instanceof HTMLElement))return;
  const marks=new Set(inherited),tag=node.tagName;
  if(tag==='B'||tag==='STRONG'||node.style.fontWeight==='bold'||Number(node.style.fontWeight)>=600)marks.add('bold');
  if(node.style.fontWeight==='normal'||node.style.fontWeight==='400')marks.delete('bold');
  if(tag==='I'||tag==='EM'||node.style.fontStyle==='italic')marks.add('italic');
  if(node.style.fontStyle==='normal')marks.delete('italic');
  if(tag==='U'||node.style.textDecoration.includes('underline'))marks.add('underline');
  if(tag==='BR') {
   // Browser-created final BR is a caret scaffold, not another blank paragraph.
   let previous=node.previousSibling,next=node.nextSibling;
   while(previous?.nodeType===Node.TEXT_NODE&&!previous.textContent)previous=previous.previousSibling;
   while(next?.nodeType===Node.TEXT_NODE&&!next.textContent)next=next.nextSibling;
   if(!next&&(!previous||previous.nodeName==='BR'))return;
   units.push({text:'\n',marks:[],node:node.parentNode!,offset:[...node.parentNode!.childNodes].indexOf(node)});return;
  }
  if(node!==host&&['DIV','P'].includes(tag)&&(units.length||node.previousSibling))units.push({text:'\n',marks:[],node,offset:0});
  for(const child of node.childNodes)collect(child,order.filter(mark=>marks.has(mark)));
 };
 collect(host,[]);
 // Match the existing Markdown contract: line-edge whitespace stays outside
 // marks, while the exact text and blank lines are preserved.
 for(let start=0;start<units.length;) {
  let end=start;while(end<units.length&&units[end]!.text!=='\n')end++;
  let left=start,right=end-1;
  while(left<end&&/\s/.test(units[left]!.text))units[left++]!.marks=[];
  while(right>=left&&/\s/.test(units[right]!.text))units[right--]!.marks=[];
  start=end+1;
 }
 let value='',active:Mark[]=[],plain=0;const points:Point[]=[];
 const transition=(next:Mark[])=>{
  let shared=0;while(shared<active.length&&active[shared]===next[shared])shared++;
  for(let i=active.length-1;i>=shared;i--)value+=marker[active[i]!];
  for(let i=shared;i<next.length;i++)value+=marker[next[i]!];active=next;
 };
 for(const unit of units) {
  transition(unit.text==='\n'?[]:unit.marks);points.push({node:unit.node,offset:unit.offset,source:value.length,plain});
  value+=unit.text;plain++;
  points.push({node:unit.node,offset:unit.node.nodeType===Node.TEXT_NODE?unit.offset+1:unit.offset,source:value.length,plain});
 }
 transition([]);return {value,points};
}
/** Render-time comparison canonicalizes native B/I/SPAN variants without
 * changing the mounted editor, caret or undo history. */
export function normalizeMessageInput(node:HTMLElement):void {paint(node,snapshot(node).value);node.style.removeProperty('height');node.style.removeProperty('overflow-y');if(!node.getAttribute('style'))node.removeAttribute('style');}
export function createMessageInput(label:string,active:()=>boolean):MessageInput {
 const host=document.createElement('div') as MessageInput;host.className='message-input';host.contentEditable='true';
 host.setAttribute('role','textbox');host.setAttribute('aria-label',label);host.setAttribute('aria-multiline','true');host.dataset.placeholder=label==='Сообщение'?label:'';
 let original:string|null='',readOnly=false,disabled=false;
 let cached:ReturnType<typeof snapshot>|null=null;
 const currentSnapshot=()=>cached??(cached=snapshot(host));
 const changed=()=>{original=null;cached=null;host.dataset.empty=String(!currentSnapshot().value.length);};
 host.addEventListener('input',changed);
 const position=(node:Node|null,offset:number):number=>{
  const current=currentSnapshot();if(!node||!host.contains(node))return current.value.length;
  const exact=current.points.find(point=>point.node===node&&point.offset===offset);if(exact)return exact.source;
  try{const range=document.createRange();range.selectNodeContents(host);range.setEnd(node,offset);const length=range.toString().length;return current.points.find(point=>point.plain>=length)?.source??current.value.length;}catch{return current.value.length;}
 };
 const selection=():[number,number]=>{const s=window.getSelection();if(!s?.rangeCount)return [host.value.length,host.value.length];const range=s.getRangeAt(0);return [position(range.startContainer,range.startOffset),position(range.endContainer,range.endOffset)];};
 const setSelectionRange=(start:number,end:number)=>{
  const current=currentSnapshot(),point=(at:number)=>current.points.find(p=>p.source>=at)??current.points.at(-1);
  const a=point(Math.max(0,start)),b=point(Math.max(start,end)),range=document.createRange();
  if(a&&b){range.setStart(a.node,Math.min(a.offset,a.node.nodeType===Node.TEXT_NODE?a.node.textContent!.length:a.node.childNodes.length));range.setEnd(b.node,Math.min(b.offset,b.node.nodeType===Node.TEXT_NODE?b.node.textContent!.length:b.node.childNodes.length));}else{range.selectNodeContents(host);range.collapse(false);}
  const selected=window.getSelection();selected?.removeAllRanges();selected?.addRange(range);
 };
 Object.defineProperties(host,{
  value:{get:()=>original??currentSnapshot().value,set:(value:string)=>{original=String(value);paint(host,original);cached=null;host.dataset.empty=String(!original.length);}},
  selectionStart:{get:()=>selection()[0]},selectionEnd:{get:()=>selection()[1]},
  readOnly:{get:()=>readOnly,set:(value:boolean)=>{readOnly=value;host.contentEditable=String(!readOnly&&!disabled);host.setAttribute('aria-readonly',String(readOnly));}},
  disabled:{get:()=>disabled,set:(value:boolean)=>{disabled=value;host.contentEditable=String(!readOnly&&!disabled);host.setAttribute('aria-disabled',String(disabled));}},
 });
 host.setSelectionRange=setSelectionRange;
 host.setRangeText=(value,start,end)=>{
  host.focus();setSelectionRange(start,end);document.execCommand('insertText',false,value);changed();
 };
 // Native command history preserves selection, undo and IME composition. App
 // key handlers still own Enter/send, mentions and empty ArrowUp editing.
 host.addEventListener('keydown',event=>{
  const format=formattingShortcut(event);if(!format||event.defaultPrevented||readOnly||disabled||!active())return;
  event.preventDefault();if(event.repeat)return;document.execCommand(format);changed();host.dispatchEvent(new Event('input',{bubbles:true}));
 });
 host.addEventListener('beforeinput',event=>{
  if(!event.isComposing&&event.inputType==='insertParagraph'){event.preventDefault();document.execCommand('insertLineBreak');}
 });
 host.addEventListener('paste',event=>{
  if(readOnly||disabled||!active()){event.preventDefault();return;}
  if(event.clipboardData?.files.length){event.preventDefault();return;}
  event.preventDefault();document.execCommand('insertText',false,event.clipboardData?.getData('text/plain')??'');
 });
 host.addEventListener('drop',event=>{
  if(readOnly||disabled||!active()){event.preventDefault();return;}
  if(event.dataTransfer?.files.length){event.preventDefault();return;}
  event.preventDefault();document.execCommand('insertText',false,event.dataTransfer?.getData('text/plain')??'');
 });
 host.value='';return host;
}
